import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { resolvePublicFileUrlForImg } from '@/config/apiBaseUrl';
import { MEDICREW_CERTIFICATE_FOOTER_LOGO_PATH } from '@/lib/certificateIssuerAssets';
import type { ClassEvaluationStats, ClassEvaluationUser } from '@/types/api/evaluation';

export interface EvaluationResultsPdfData {
	className: string;
	scenarioName: string;
	dateRange?: string;
	stats: ClassEvaluationStats;
	users: ClassEvaluationUser[];
}

const PDF_FONT_STACK = "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Pretendard', 'Noto Sans KR', sans-serif";

/** A4 @ 96dpi — html2canvas 캡처 기준 너비 */
const CAPTURE_WIDTH_PX = 794;

/** 브랜드 primary 컬러 */
const PRIMARY_COLOR = '#1a4480';

const PDF_COLORS = {
	primary: PRIMARY_COLOR,
	text: '#111827',
	muted: '#6b7280',
	border: '#e5e7eb',
	background: '#ffffff',
	cardBg: '#f9fafb',
} as const;

function resolveImageUrl(path: string): string | null {
	try {
		return resolvePublicFileUrlForImg(path) ?? null;
	} catch {
		return null;
	}
}

/** 밀리초를 시간 형식으로 변환 */
function formatPlayTime(milliseconds: number | null | undefined): string {
	if (milliseconds == null || milliseconds === 0) return '0h 0m 0s';
	
	const totalSeconds = Math.floor(milliseconds / 1000);
	const hours = Math.floor(totalSeconds / 3600);
	const minutes = Math.floor((totalSeconds % 3600) / 60);
	const secs = totalSeconds % 60;
	
	return `${hours}h ${minutes}m ${secs}s`;
}

/** 헤더: 메디크루 로고 */
function appendHeaderLogo(parent: HTMLElement): void {
	const header = document.createElement('div');
	header.style.cssText = 'padding: 24px 48px; text-align: left;';

	const logo = document.createElement('img');
	logo.src = MEDICREW_CERTIFICATE_FOOTER_LOGO_PATH;
	logo.alt = 'medicrew';
	logo.crossOrigin = 'anonymous'; // CORS 문제 해결
	logo.style.cssText = 'height: 24px; width: auto; object-fit: contain;';
	
	header.appendChild(logo);
	parent.appendChild(header);
}

/** 타이틀: 클래스명 + 시나리오명 */
function appendTitle(parent: HTMLElement, className: string, scenarioName: string, dateRange?: string): void {
	const titleSection = document.createElement('div');
	titleSection.style.cssText = 'padding: 0 48px 24px;';

	const classTitle = document.createElement('h1');
	classTitle.textContent = className;
	classTitle.style.cssText = `margin: 0 0 8px; font-size: 24px; font-weight: 700; color: ${PDF_COLORS.primary};`;
	
	const scenarioTitle = document.createElement('h2');
	scenarioTitle.textContent = scenarioName;
	scenarioTitle.style.cssText = `margin: 0; font-size: 18px; font-weight: 600; color: ${PDF_COLORS.text};`;

	titleSection.appendChild(classTitle);
	titleSection.appendChild(scenarioTitle);

	if (dateRange) {
		const dateText = document.createElement('p');
		dateText.textContent = `조회기간: ${dateRange}`;
		dateText.style.cssText = `margin: 8px 0 0; font-size: 14px; color: ${PDF_COLORS.muted};`;
		titleSection.appendChild(dateText);
	}

	parent.appendChild(titleSection);
}

/** 통계 카드 4개 */
function appendStatsCards(parent: HTMLElement, stats: ClassEvaluationStats): void {
	const cardsContainer = document.createElement('div');
	cardsContainer.style.cssText = 'display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; padding: 0 48px 24px;';

	const cards = [
		{ label: '전체 참여자수', value: `${stats.total_participants}명` },
		{ label: '완료', value: `${stats.completed_participants}명 (${stats.completion_rate}%)` },
		{ label: '총 실습횟수', value: `${stats.total_play_count}회` },
		{ label: '총 실습시간', value: formatPlayTime(stats.total_play_time) },
	];

	cards.forEach(card => {
		const cardElement = document.createElement('div');
		cardElement.style.cssText = `background: ${PDF_COLORS.cardBg}; border: 1px solid ${PDF_COLORS.border}; border-radius: 8px; padding: 16px;`;

		const label = document.createElement('div');
		label.textContent = card.label;
		label.style.cssText = `font-size: 12px; color: ${PDF_COLORS.muted}; margin-bottom: 8px;`;

		const value = document.createElement('div');
		value.textContent = card.value;
		value.style.cssText = `font-size: 18px; font-weight: 700; color: ${PDF_COLORS.text};`;

		cardElement.appendChild(label);
		cardElement.appendChild(value);
		cardsContainer.appendChild(cardElement);
	});

	parent.appendChild(cardsContainer);
}

/** 평가결과 테이블 */
function appendResultsTable(parent: HTMLElement, users: ClassEvaluationUser[]): void {
	const tableContainer = document.createElement('div');
	tableContainer.style.cssText = 'padding: 0 48px 32px;';

	const table = document.createElement('table');
	table.style.cssText = `width: 100%; border-collapse: collapse; font-size: 12px; border: 1px solid ${PDF_COLORS.border};`;

	// 헤더
	const thead = document.createElement('thead');
	const headerRow = document.createElement('tr');
	headerRow.style.cssText = `background: ${PDF_COLORS.cardBg};`;

	const headers = ['학습자명', '아이디(이메일)', '실습횟수', '실습시간', '최신점수', '최고점수', '최저점수', '상태'];
	headers.forEach(headerText => {
		const th = document.createElement('th');
		th.textContent = headerText;
		th.style.cssText = `padding: 12px 8px; text-align: left; font-weight: 600; color: ${PDF_COLORS.text}; border-bottom: 2px solid ${PDF_COLORS.border};`;
		headerRow.appendChild(th);
	});
	thead.appendChild(headerRow);
	table.appendChild(thead);

	// 본문
	const tbody = document.createElement('tbody');
	users.forEach((user, index) => {
		const row = document.createElement('tr');
		row.style.cssText = index % 2 === 0 ? 'background: #ffffff;' : `background: ${PDF_COLORS.cardBg};`;

		const cells = [
			user.user_name,
			user.user_email,
			`${user.play_count}회`,
			formatPlayTime(user.total_play_time),
			user.latest_score !== null ? `${user.latest_score}점` : '—',
			user.highest_score !== null ? `${user.highest_score}점` : '—',
			user.lowest_score !== null ? `${user.lowest_score}점` : '—',
			user.status || '—',
		];

		cells.forEach(cellText => {
			const td = document.createElement('td');
			td.textContent = cellText;
			td.style.cssText = `padding: 10px 8px; color: ${PDF_COLORS.text}; border-bottom: 1px solid ${PDF_COLORS.border};`;
			row.appendChild(td);
		});

		tbody.appendChild(row);
	});
	table.appendChild(tbody);

	tableContainer.appendChild(table);
	parent.appendChild(tableContainer);
}

/** 푸터: medicrew 로고만 */
function appendPoweredByFooter(parent: HTMLElement): void {
	const footer = document.createElement('div');
	footer.style.cssText = [
		'box-sizing: border-box',
		'width: 100%',
		`background: ${PRIMARY_COLOR}`,
		'-webkit-print-color-adjust: exact',
		'print-color-adjust: exact',
		'padding: 20px 48px',
		'text-align: center',
	].join(';');

	// medicrew 로고만 표시 (직접 경로 사용)
	const footerLogo = document.createElement('img');
	footerLogo.src = MEDICREW_CERTIFICATE_FOOTER_LOGO_PATH;
	footerLogo.alt = 'medicrew';
	footerLogo.crossOrigin = 'anonymous'; // CORS 문제 해결
	footerLogo.style.cssText = 'display: block; margin: 0 auto; height: 20px; width: auto; object-fit: contain;';
	
	footer.appendChild(footerLogo);
	parent.appendChild(footer);
}

/** PDF 캡처용 HTML 요소 생성 */
export function buildEvaluationResultsCaptureElement(data: EvaluationResultsPdfData): HTMLElement {
	const root = document.createElement('div');
	root.setAttribute('data-evaluation-pdf-capture', 'true');
	
	root.style.cssText = [
		`box-sizing: border-box`,
		`background: ${PDF_COLORS.background}`,
		`font-family: ${PDF_FONT_STACK}`,
		`color: ${PDF_COLORS.text}`,
		`position: fixed`,
		`left: -10000px`,
		`top: 0`,
		`width: ${CAPTURE_WIDTH_PX}px`,
		`min-height: 1123px`,
		`display: flex`,
		`flex-direction: column`,
	].join(';');

	const content = document.createElement('div');
	content.style.cssText = 'flex: 1; display: flex; flex-direction: column;';

	appendHeaderLogo(content);
	appendTitle(content, data.className, data.scenarioName, data.dateRange);
	appendStatsCards(content, data.stats);
	appendResultsTable(content, data.users);

	root.appendChild(content);
	appendPoweredByFooter(root);

	return root;
}

/** PDF 파일명 생성 */
export function buildEvaluationResultsFileName(
	className: string,
	scenarioName: string
): string {
	const safeClassName = className.replace(/[/\\?%*:|"<>]/g, '_').slice(0, 40);
	const safeScenarioName = scenarioName.replace(/[/\\?%*:|"<>]/g, '_').slice(0, 40);
	const timestamp = new Date().toISOString().slice(0, 10);
	return `평가결과_${safeClassName}_${safeScenarioName}_${timestamp}.pdf`;
}

/**
 * 평가결과 PDF 생성 및 다운로드
 */
export async function generateEvaluationResultsPdf(data: EvaluationResultsPdfData): Promise<void> {
	const captureElement = buildEvaluationResultsCaptureElement(data);
	document.body.appendChild(captureElement);

	try {
		// DOM 렌더링 대기
		await new Promise<void>((resolve) => {
			window.setTimeout(resolve, 300);
		});

		// HTML을 캔버스로 캡처
		const canvas = await html2canvas(captureElement, {
			scale: 2,
			useCORS: true,
			logging: false,
			backgroundColor: PDF_COLORS.background,
			width: CAPTURE_WIDTH_PX,
		});

		// PDF 생성
		const imgData = canvas.toDataURL('image/png');
		const pdf = new jsPDF('p', 'mm', 'a4');
		const pageWidth = pdf.internal.pageSize.getWidth();
		const pageHeight = pdf.internal.pageSize.getHeight();
		
		// 캔버스 비율에 맞춰 PDF 높이 계산
		const imgHeight = (canvas.height * pageWidth) / canvas.width;
		
		if (imgHeight <= pageHeight) {
			// 한 페이지에 들어가는 경우
			pdf.addImage(imgData, 'PNG', 0, 0, pageWidth, imgHeight);
		} else {
			// 여러 페이지로 나눠야 하는 경우
			let heightLeft = imgHeight;
			let position = 0;

			pdf.addImage(imgData, 'PNG', 0, position, pageWidth, imgHeight);
			heightLeft -= pageHeight;

			while (heightLeft > 0) {
				position = heightLeft - imgHeight;
				pdf.addPage();
				pdf.addImage(imgData, 'PNG', 0, position, pageWidth, imgHeight);
				heightLeft -= pageHeight;
			}
		}

		const fileName = buildEvaluationResultsFileName(
			data.className,
			data.scenarioName
		);
		pdf.save(fileName);

		console.log('✅ [EvaluationResults] PDF 다운로드 성공:', fileName);
	} catch (error) {
		console.error('❌ [EvaluationResults] PDF 생성 실패:', error);
		throw error;
	} finally {
		document.body.removeChild(captureElement);
	}
}
